import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ContainerScroll } from '../components/ui/container-scroll-animation';

const ShowcasePage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [stats, setStats] = useState({
    totalAssociates: 2790,
    activeMembers: 1685,
    attiGestiti: 150,
    voucherAttivi: 45,
    certificatiAttivi: 320
  });

  // Animazione contatore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [counts, setCounts] = useState({
    totalAssociates: 0,
    activeMembers: 0,
    attiGestiti: 0,
    voucherAttivi: 0,
    certificatiAttivi: 0
  });

  useEffect(() => {
    const duration = 2000; // 2 secondi
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      
      setCounts({
        totalAssociates: Math.floor(stats.totalAssociates * progress),
        activeMembers: Math.floor(stats.activeMembers * progress),
        attiGestiti: Math.floor(stats.attiGestiti * progress),
        voucherAttivi: Math.floor(stats.voucherAttivi * progress),
        certificatiAttivi: Math.floor(stats.certificatiAttivi * progress)
      });

      if (step >= steps) {
        clearInterval(timer);
        setCounts(stats);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [stats]);

  const features = [
    {
      title: 'Svincolo Atti',
      description: 'Sistema completo per la gestione degli atti di vendita con workflow automatizzato',
      icon: '📄',
      color: 'from-blue-500 to-cyan-500',
      highlights: [
        'Richiesta online con selezione studi',
        'Gestione preventivi multipli',
        'Upload documenti sicuro',
        'Notifiche email automatiche',
        'Tracking stato in tempo reale'
      ]
    },
    {
      title: 'Rete Certificata',
      description: 'Gestione completa dei membri certificati con mappa interattiva',
      icon: '🏆',
      color: 'from-green-500 to-emerald-500',
      highlights: [
        'Iscrizione online con validazione',
        'Gestione certificazioni GDPR/Cyber',
        'Mappa interattiva geolocalizzata',
        'Area riservata membri',
        'Dashboard statistiche'
      ]
    },
    {
      title: 'Voucher Rimborso',
      description: 'Marketplace interno per scambio voucher tra associati',
      icon: '💰',
      color: 'from-purple-500 to-pink-500',
      highlights: [
        'Creazione e gestione voucher',
        'Sistema di prenotazione',
        'Chat integrata per trattative',
        'Notifiche in tempo reale',
        'Storico transazioni'
      ]
    },
    {
      title: 'Gestione Associati',
      description: 'Database completo con oltre 2.790 associati',
      icon: '👥',
      color: 'from-orange-500 to-red-500',
      highlights: [
        'Import CSV automatizzato',
        'Ricerca avanzata e filtri',
        'Gestione stati e categorie',
        'Export dati personalizzato',
        'Statistiche in tempo reale'
      ]
    },
    {
      title: 'Blog & Gallerie',
      description: 'CMS completo per contenuti e media',
      icon: '📰',
      color: 'from-indigo-500 to-blue-500',
      highlights: [
        'Editor WYSIWYG avanzato',
        'Gestione immagini ottimizzate',
        'SEO-friendly',
        'Categorie e tag',
        'Statistiche visualizzazioni'
      ]
    },
    {
      title: 'Area Admin',
      description: 'Dashboard completa per amministrazione',
      icon: '⚙️',
      color: 'from-gray-500 to-slate-500',
      highlights: [
        'Dashboard con metriche live',
        'Gestione utenti e permessi',
        'Email analytics',
        'Audit log completo',
        'Configurazioni sistema'
      ]
    }
  ];

  const techStack = [
    { name: 'MongoDB', icon: '🍃', category: 'Database' },
    { name: 'Express.js', icon: '⚡', category: 'Backend' },
    { name: 'React', icon: '⚛️', category: 'Frontend' },
    { name: 'Node.js', icon: '🟢', category: 'Runtime' },
    { name: 'TypeScript', icon: '📘', category: 'Language' },
    { name: 'GraphQL', icon: '◆', category: 'API' },
    { name: 'Tailwind CSS', icon: '🎨', category: 'Styling' },
    { name: 'JWT', icon: '🔐', category: 'Auth' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-black dark:to-gray-900">
      
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-unasca-green/10 via-transparent to-blue-500/10"></div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-8"
            >
              <img
                src="/images/logo-unasca.png"
                alt="UNASCA"
                className="h-24 mx-auto mb-6"
              />
            </motion.div>
            <br />
            <h1 className="text-5xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-unasca-green via-blue-600 to-purple-600 bg-clip-text text-transparent">
              Nuovo portale per gli Studi
            </h1>
            <br />
            <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-300 mb-8">
              Piattaforma moderna e completa per la gestione associativa
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <span className="px-6 py-3 bg-unasca-green text-white rounded-full font-semibold shadow-lg">
                Stack MERN
              </span>
              <span className="px-6 py-3 bg-blue-600 text-white rounded-full font-semibold shadow-lg">
                TypeScript
              </span>
              <span className="px-6 py-3 bg-purple-600 text-white rounded-full font-semibold shadow-lg">
                GraphQL
              </span>
            </div>
          </motion.div>

          {/* Statistiche Animate 
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-5 gap-6 max-w-5xl mx-auto"
          >
            {[
              { label: 'Associati Totali', value: counts.totalAssociates, icon: '👥', color: 'blue' },
              { label: 'Studi Attivi', value: counts.activeMembers, icon: '🏢', color: 'green' },
              { label: 'Atti Gestiti', value: counts.attiGestiti, icon: '📄', color: 'purple' },
              { label: 'Voucher Attivi', value: counts.voucherAttivi, icon: '💰', color: 'orange' },
              { label: 'Certificati', value: counts.certificatiAttivi, icon: '🏆', color: 'red' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-700"
              >
                <div className="text-4xl mb-2">{stat.icon}</div>
                <div className={`text-3xl font-bold text-${stat.color}-600 dark:text-${stat.color}-400 mb-1`}>
                  {stat.value.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
          */}
        </div>
      </section>

      {/* Stack Tecnologico */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
              Stack Tecnologico Moderno
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Architettura MERN con le migliori tecnologie
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {techStack.map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 text-center cursor-pointer"
              >
                <div className="text-5xl mb-3">{tech.icon}</div>
                <h3 className="font-bold text-lg mb-1 text-gray-900 dark:text-white">{tech.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{tech.category}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Funzionalità Chiave */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-black dark:to-gray-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
              Funzionalità Principali
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Sistema completo e integrato per la gestione associativa
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden relative group"
              >
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>

                <div className="relative z-10">
                  <div className="text-6xl mb-4">{feature.icon}</div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {feature.description}
                  </p>

                  <ul className="space-y-2">
                    {feature.highlights.map((highlight, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <span className="text-unasca-green mt-1">✓</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Architettura e Sicurezza */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6 text-gray-900 dark:text-white">
                Architettura Moderna e Sicura
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center text-2xl">
                    🔒
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Autenticazione Sicura</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      JWT tokens, OTP via email, sessioni protette, rate limiting
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center text-2xl">
                    ⚡
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Performance Ottimizzate</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Lazy loading, caching, query ottimizzate, immagini responsive
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center text-2xl">
                    📱
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Mobile First</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Design responsive, touch-friendly, PWA ready
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center text-2xl">
                    📊
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Analytics Integrate</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Dashboard real-time, email tracking, audit log completo
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-unasca-green to-blue-600 rounded-2xl p-8 text-white shadow-2xl"
            >
              <h3 className="text-3xl font-bold mb-6">Dati del Progetto</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-white/20">
                  <span className="text-lg">Linee di Codice</span>
                  <span className="text-2xl font-bold">~50.000+</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/20">
                  <span className="text-lg">Componenti React</span>
                  <span className="text-2xl font-bold">80+</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/20">
                  <span className="text-lg">API Endpoints</span>
                  <span className="text-2xl font-bold">60+</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/20">
                  <span className="text-lg">Modelli Database</span>
                  <span className="text-2xl font-bold">15+</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/20">
                  <span className="text-lg">Immagini Migrate</span>
                  <span className="text-2xl font-bold">592</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-lg">Tempo Sviluppo</span>
                  <span className="text-2xl font-bold">6+ mesi</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sezione Device Mockups - Responsive & Dark Mode */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-unasca-green via-blue-600 to-purple-600 bg-clip-text text-transparent">
              Esperienza Utente Eccezionale
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Design moderno, responsive e accessibile su ogni dispositivo
            </p>
          </motion.div>

          {/* iPad - Responsive Design - Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mb-24"
          >
            {/* Testo introduttivo centrato */}
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-sm font-semibold mb-4">
                📱 Responsive Design
              </div>
              <h3 className="text-3xl lg:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
                Perfetto su Ogni Dispositivo
              </h3>
              <p className="text-lg lg:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                Il portale si adatta automaticamente a <strong>desktop, tablet e smartphone</strong>,
                garantendo un'esperienza fluida e intuitiva ovunque ti trovi.
              </p>
            </div>

            {/* Immagine iPad a larghezza piena */}
            <motion.div
              whileHover={{ scale: 1.21 }}
              transition={{ duration: 0.3 }}
              className="mb-12"
            >
              <img
                src="/images/ipad.png"
                alt="Portale UNASCA su iPad - Design Responsive"
                className="w-full max-w-6xl mx-auto drop-shadow-2xl rounded-2xl"
              />
            </motion.div>

            {/* Features in grid sotto l'immagine */}
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-4">✅</div>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Layout Fluido</h4>
                <p className="text-gray-600 dark:text-gray-400">Si adatta a qualsiasi risoluzione schermo, da smartphone a desktop 4K</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-4">👆</div>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Touch Optimized</h4>
                <p className="text-gray-600 dark:text-gray-400">Interfaccia ottimizzata per il tocco con pulsanti e aree cliccabili generose</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-4">⚡</div>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Performance Mobile</h4>
                <p className="text-gray-600 dark:text-gray-400">Caricamento veloce anche su connessioni 4G, immagini ottimizzate</p>
              </div>
            </div>
          </motion.div>

          {/* iPhone - Dark Mode */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mb-24"
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                whileHover={{ scale: 1.42 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="/images/iphone.png"
                  alt="Portale UNASCA su iPhone - Dark Mode"
                  className="w-full max-w-sm mx-auto drop-shadow-2xl"
                />
              </motion.div>
              <div>
                <div className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full text-sm font-semibold mb-4">
                  🌙 Dark Mode
                </div>
                <h3 className="text-3xl lg:text-4xl font-bold mb-6 text-gray-900 dark:text-white">
                  Modalità Scura Integrata
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  Il portale include una <strong>modalità scura elegante</strong> che riduce l'affaticamento
                  visivo e migliora la leggibilità in ambienti con poca luce.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">🎨</span>
                    <div>
                      <strong className="text-gray-900 dark:text-white">Design Elegante</strong>
                      <p className="text-gray-600 dark:text-gray-400">Palette colori studiata per il comfort visivo</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">👁️</span>
                    <div>
                      <strong className="text-gray-900 dark:text-white">Riduce Affaticamento</strong>
                      <p className="text-gray-600 dark:text-gray-400">Perfetto per sessioni di lavoro prolungate</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">🔄</span>
                    <div>
                      <strong className="text-gray-900 dark:text-white">Switch Automatico</strong>
                      <p className="text-gray-600 dark:text-gray-400">Si adatta alle preferenze del sistema</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* iPad Front - Servizi Rete Certificata con ContainerScroll */}
          <div className="mb-12">
            <ContainerScroll
              titleComponent={
                <div className="text-center mb-8">
                  <div className="inline-block px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full text-sm font-semibold mb-4">
                    🏆 Rete Certificata
                  </div>
                  <h3 className="text-3xl lg:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
                    Servizi Esclusivi per i Membri della Rete
                  </h3>
                  <p className="text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
                    La <strong>Rete Certificata UNASCA</strong> offre un ecosistema completo di servizi dedicati:
                    dalla gestione atti al rimborso dei voucher, dalla mappa interattiva alla dashboard personalizzata.
                  </p>

                  {/* Highlights in grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 max-w-5xl mx-auto">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                      <div className="text-4xl mb-3">📄</div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-2">Svincolo atti</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Workflow automatizzato con preventivi multipli</p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                      <div className="text-4xl mb-3">🎫</div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-2">Rimborso voucher</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Rimborsi e benefit esclusivi</p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                      <div className="text-4xl mb-3">🗺️</div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-2">Mappa interattiva</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Trova studi in tutta Italia</p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                      <div className="text-4xl mb-3">📊</div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-2">Dashboard</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Gestione completa del profilo</p>
                    </div>
                  </div>
                </div>
              }
            >
              <img
                src="/images/Screenshot 2025-11-09 060944.jpg"
                alt="Servizi Rete Certificata UNASCA"
                className="w-full h-full object-cover object-left-top rounded-2xl"
              />
            </ContainerScroll>
          </div>

          {/* Statistiche Finali */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
                <div className="text-4xl font-bold text-unasca-green mb-2">100%</div>
                <div className="text-gray-600 dark:text-gray-400">Mobile Friendly</div>
              </div>
              <div className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
                <div className="text-4xl font-bold text-blue-600 mb-2">&lt;2s</div>
                <div className="text-gray-600 dark:text-gray-400">Caricamento</div>
              </div>
              <div className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
                <div className="text-4xl font-bold text-purple-600 mb-2">A+</div>
                <div className="text-gray-600 dark:text-gray-400">Accessibilità</div>
              </div>
              <div className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
                <div className="text-4xl font-bold text-green-600 mb-2">99.9%</div>
                <div className="text-gray-600 dark:text-gray-400">Uptime</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Finale */}
      <section className="py-20 bg-gradient-to-br from-unasca-green via-blue-600 to-purple-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Pronto per il Futuro
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
              Una piattaforma moderna, scalabile e sicura per supportare la crescita di UNASCA
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/"
                className="px-8 py-4 bg-white text-unasca-green rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
              >
                Esplora il Sito
              </Link>
              <Link
                to="/admin"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white text-white rounded-full font-bold text-lg hover:bg-white/20 transition-all"
              >
                Area Admin
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ShowcasePage;

